import {SOURCES} from "@/config/layoutConfig";
import {nextTick} from '@vue/composition-api'

const STORE_ACTIONS = {
    ADD_NEW_TAB: 'grid/addNewTab',
    REMOVE_TAB: 'grid/removeTab',
    SET_ACTIVE_TAB: 'grid/setActiveTab',
    ADD_ITEM_TO_LAYOUT: 'grid/addItemToLayout',
    UPDATE_SLOT_DATA: 'grid/updateSlotData',
    SET_SLOT_DATA: 'grid/setSlotData',
    RESET_SLOT_SELECTION: 'grid/resetSlotSelection'
};

const STORE_GETTERS = {
    ACTIVE_TAB_ID: 'grid/activeTabId',
    CURRENT_TAB: 'grid/currentTab',
    TABS: 'grid/tabs',
    USER_LAYOUTS: 'layouts/getUserLayouts'
};

const STORE_MUTATIONS = {
    UNSET_LAYOUT_SLOT: 'grid/unsetLayoutSlot'
};

export default class {
    constructor(store) {
        this.logPrefix = '[LayoutApi]'
        this.store = store;
    }

    /**
     * Add a new tab
     *
     * @param {String} name
     * @param {Number|String} layoutId
     * @param {Object} tabData
     * @returns Promise
     */
    addNewTab(name = '', layoutId = 'default', tabData = null) {
        return this.store.dispatch(STORE_ACTIONS.ADD_NEW_TAB, {tabName: name, layout: layoutId, viewData: tabData});
    }

    /**
     * Remove/Close a tab by id
     *
     * @param {String} tabId
     */
    removeTab(tabId) {
        if (!tabId) {
            console.error(this.logPrefix, 'removeTab missing required param [tabId]');
            return;
        }
        this.store.dispatch(STORE_ACTIONS.REMOVE_TAB, tabId);
    }

    /**
     * Returns the id of the current active tab
     *
     * @returns String
     */
    getCurrentTabId() {
        return this.store.getters[STORE_GETTERS.ACTIVE_TAB_ID];
    }

    /**
     * Return the data of the current active tab
     *
     * @returns Object
     */
    getCurrentTabData() {
        return this.store.getters[STORE_GETTERS.CURRENT_TAB];
    }

    checkIfTabExists(id) {
        return this.store.getters[STORE_GETTERS.TABS].hasOwnProperty(id);
    }

    /**
     * Set the active tab by id
     *
     * @param {String} tabId
     */
    setActiveTab(tabId = null) {
        this.store.dispatch(STORE_ACTIONS.SET_ACTIVE_TAB, tabId);
    }

    /**
     * Create an object with the necessary slot structure
     *
     * @param {String} name
     * @param {String} position
     * @param {Object|Array} data
     * @param {String} type
     * @returns Object|null
     */
    buildSlot(name = null, position = null, data = null, type = null) {
        if ([name, position, type].includes(null)) {
            console.error(this.logPrefix, 'buildSlot: missing required params');
            return null;
        }
        return {
            name,
            pos: position,
            data,
            type
        }
    }

    /**
     * Set data for the slot in the current active tab in the given position
     *
     * Note: For the slotType value check the file: src/config/layoutConfig.js
     *
     * @param {String} slotName
     * @param {Number} slotPosition
     * @param {Object|Array} slotData
     * @param {String} slotType
     */
    setSlot(slotName = '', slotPosition = 1, slotData = null, slotType = null) {
        const slot = this.buildSlot(slotName, slotPosition, slotData, slotType);
        if (!slot) return;

        nextTick(() => this.store.commit(STORE_MUTATIONS.UNSET_LAYOUT_SLOT, slot.pos));
        nextTick(() => this.store.dispatch(STORE_ACTIONS.ADD_ITEM_TO_LAYOUT, slot));
    }

    /**
     * Empty slot in the given position
     *
     * @param {String|Number} slotPosition
     */
    unsetSlot(slotPosition) {
        this.store.commit(STORE_MUTATIONS.UNSET_LAYOUT_SLOT, slotPosition);
    }

    /**
     * Find slot by position or name in the current tab
     *
     * @param {String|Number} identifier
     * @returns {Object|null}
     */
    findSlotByIdentifier(identifier) {
        const tab = this.getCurrentTabData();
        if (!tab?.slots) return null;

        return tab.slots.find(slot => slot.pos === identifier || slot.name === identifier) || null;
    }

    /**
     * Search and retrieve the given slot by position or name
     *
     * @param {String|Number} identifier
     * @returns {Object|null}
     */
    getSlotData(identifier) {
        return this.findSlotByIdentifier(identifier);
    }

    /**
     * Update the data for slot in the current active tab in the given position and type
     *
     * Note: For the slotType value check the file: src/config/layoutConfig.js
     *
     * @param {Number} slotPosition
     * @param {String} slotType
     * @param {Object|Array} slotData
     */
    updateSlotData(slotPosition, slotType, slotData) {
        const tab = this.getCurrentTabData();
        if (!tab?.slots) return;

        const slot = tab.slots.find(slot => slot.pos === slotPosition && slot.type === slotType);
        if (slot) {
            this.store.dispatch(STORE_ACTIONS.UPDATE_SLOT_DATA, {
                pos: slotPosition,
                data: slotData,
            });
        }
    }

    setSlotData(type, name, data, source = null) {
        this.store.dispatch(STORE_ACTIONS.SET_SLOT_DATA, {
            type,
            name,
            data: {
                ...JSON.parse(JSON.stringify(data)),
                in_app_source: source,
            },
            source: source || SOURCES.ALARM
        });
    }

    getLayoutDataById(layoutId) {
        const userLayouts = this.store.getters[STORE_GETTERS.USER_LAYOUTS];
        if (!userLayouts) return null;

        return userLayouts.find(layout => layout.id === layoutId) || null;
    }

    getTabs() {
      return this.store.getters[STORE_GETTERS.TABS];
    }

    selectLastTab() {
      if (this.getTabs()) {
        this.setActiveTab(Object.keys(this.getTabs())[Object.keys(this.getTabs()).length - 1]);
      }
    }

    resetSlotSelection() {
        this.store.dispatch(STORE_ACTIONS.RESET_SLOT_SELECTION);
    }
}
